/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import VsCodeTelemetryReporter from '@vscode/extension-telemetry';
import * as fs from 'fs';
import * as vscode from 'vscode';
import { Api, $yhc } from './api';
import { $Chc } from './commands/commandManager';
import { $pkc } from './commands/index';
import { $zhc, IExperimentationTelemetryReporter } from './experimentTelemetryReporter';
import { $Ahc } from './experimentationService';
import { $rkc, $skc } from './lazyClientHost';
import { $Ckc } from './tsServer/cancellation.electron';
import { $Dkc } from './tsServer/logDirectoryProvider.electron';
import { $Ekc } from './tsServer/serverProcess.electron';
import { $Fkc } from './tsServer/versionProvider.electron';
import { $$jc } from './ui/activeJsTsEditorTracker';
import { $Gkc } from './configuration/configuration.electron';
import { $Hkc } from './utils/fs.electron';
import { $hic } from './logging/logger';
import { $wkc } from './utils/packageInfo';
import { $xhc } from './tsServer/plugins';
import * as temp from './utils/temp.electron';

export function activate(
	context: vscode.ExtensionContext
): Api {
	const pluginManager = new $xhc();
	context.subscriptions.push(pluginManager);

	const commandManager = new $Chc();
	context.subscriptions.push(commandManager);

	const onCompletionAccepted = new vscode.EventEmitter<vscode.CompletionItem>();
	context.subscriptions.push(onCompletionAccepted);

	const logDirectoryProvider = new $Dkc(context);
	const versionProvider = new $Fkc();

	const activeJsTsEditorTracker = new $$jc();
	context.subscriptions.push(activeJsTsEditorTracker);

	let experimentTelemetryReporter: IExperimentationTelemetryReporter | undefined;
	const packageInfo = $wkc(context);
	if (packageInfo) {
		const { name: id, version, aiKey } = packageInfo;
		const vscTelemetryReporter = new VsCodeTelemetryReporter(aiKey);
		experimentTelemetryReporter = new $zhc(vscTelemetryReporter);
		context.subscriptions.push(experimentTelemetryReporter);

		// Currently we have no experiments, but creating the service adds the appropriate
		// shared properties to the ExperimentationTelemetryReporter we just created.
		new $Ahc(experimentTelemetryReporter, id, version, context.globalState);
	}

	const logger = new $hic();

	const lazyClientHost = $rkc(context, $Hkc(), {
		pluginManager,
		commandManager,
		logDirectoryProvider,
		cancellerFactory: $Ckc,
		versionProvider,
		processFactory: new $Ekc(),
		activeJsTsEditorTracker,
		serviceConfigurationProvider: new $Gkc(),
		experimentTelemetryReporter,
		logger,
	}, item => {
		onCompletionAccepted.fire(item);
	});

	$pkc(commandManager, lazyClientHost, pluginManager, activeJsTsEditorTracker);

	import('./task/taskProvider').then(module => {
		context.subscriptions.push(module.$Ykc(lazyClientHost.map(x => x.serviceClient)));
	});

	import('./languageFeatures/tsconfig').then(module => {
		context.subscriptions.push(module.$xkc());
	});

	context.subscriptions.push($skc(lazyClientHost, pluginManager, activeJsTsEditorTracker));

	return $yhc(onCompletionAccepted.event, pluginManager);
}

export function deactivate() {
	fs.rmSync(temp.$zkc(), { recursive: true, force: true });
}
